import { ReactNode, useEffect, useState } from "react";
import { Button, ConfigProvider, Form, Table, TableColumnsType } from "antd"
import { DeleteOutlined, FormOutlined } from "@ant-design/icons";

type TEditData<T> = T & { id?: number } & Partial<{ isEdit: boolean }>
type TEditTableKey<T> = Exclude<keyof TEditData<T>, "isEdit">;

export type TOnChangeInTable<T> = {
    (idx: number, key: "isEdit", value: boolean, isApplyImmediately?: boolean): void;
    <K extends TEditTableKey<T>>(idx: number, key: K, value: TEditData<T>[K], isApplyImmediately?: boolean): void;
};
interface IProps<T> {
    groundValue?: T
    dataList: T[]
    setDataList: React.Dispatch<React.SetStateAction<T[]>>
    propsColumns?: TableColumnsType<T>
    getColumns?: (onChangeInTable: TOnChangeInTable<T>, onDeleteRow?: (idx: number) => void) => TableColumnsType<T>
    footer?: (data: readonly TEditData<T>[]) => ReactNode
    footerText?: string
    isNoEditColumn?: boolean
    bordered?: boolean
}
const CustomDynamicTable = <T extends { id?: number }>({ groundValue, dataList, setDataList, propsColumns, getColumns, isNoEditColumn = false, footer, footerText = '+ 추가', bordered = true }: IProps<T>) => {
    const [editList, setEditList] = useState<TEditData<T>[]>(dataList);

    const onChangeInTable: TOnChangeInTable<T> = (idx: number, key: keyof TEditData<T>, value: any, isApplyImmediately: boolean = false) => {
        let returnValue: TEditData<T>[] = []
        setEditList(q => {
            const newList = [...q];
            newList[idx] = { ...newList[idx], [key]: value };
            returnValue = newList
            return newList;
        });
        (isApplyImmediately || isNoEditColumn) && setDataList(q => {
            const newList = [...q]
            newList[idx] = { ...newList[idx], [key]: value };
            return newList;
        })
        return returnValue
    };
    const onSaveRow = (idx: number) => {
        const row = editList[idx];
        setDataList(
            prev => {
                const isExisting = prev.some(item => item.id === row.id);
                return isExisting
                    ? prev.map(item => (item.id === row.id ? row : item))
                    : [...prev, row];

            })
        onChangeInTable(idx, "isEdit", false);
    }

    const onRollbackInTable = (idx: number) => {
        const original = dataList.find(item => item.id === editList[idx].id);
        if (original) {
            setEditList(prev => {
                const newList = [...prev];
                newList[idx] = { ...original, isEdit: false };
                return newList;
            });
        } else {
            setEditList(prev => prev.filter((_, i) => i !== idx));
        }
    };

    const addNewRow = () => {
        const addGroundValue: TEditData<T> = { ...groundValue } as TEditData<T>;
        let addGroundValueAddKey: TEditData<T>
        setEditList(q => {
            const tempId = Math.max(...q.map(w => w.id ?? 0), 0)
            addGroundValueAddKey = { ...addGroundValue, id: (tempId + 1) }
            return [...(q ?? []), addGroundValueAddKey];
        });

        isNoEditColumn && setDataList(
            prev => {
                const isExisting = prev.some(item => item.id === addGroundValueAddKey.id);
                return isExisting
                    ? prev.map(item => (item.id === addGroundValueAddKey.id ? addGroundValueAddKey : item))
                    : [...prev, addGroundValueAddKey];

            })
    }

    const onDeleteRow = (idx: number) => {
        const row = editList[idx];
        setDataList(prev => prev.filter(item => item.id !== row.id));
        setEditList(prev => prev.filter((_, i) => i !== idx));
    };

    const columns: TableColumnsType<TEditData<T>> = [
        ...getColumns?.(onChangeInTable, onDeleteRow) ?? [],
        ...propsColumns ?? [],
        ...(isNoEditColumn
            ? []
            : [{
                title: '수정/삭제',
                dataIndex: 'isEdit',
                width: '15%',
                render: (isEdit: boolean, record: any, idx: number) => <div>
                    {isEdit
                        ? <div style={{ display: 'flex', gap: '16px', paddingInline: '16px', justifyContent: 'center', alignItems: 'center' }}>
                            <Button type="text" style={{ color: 'var(--ant-color-primary)' }} onClick={() => onSaveRow(idx)}>저장</Button>
                            <Button type="text" style={{ color: 'var(--ant-color-error)' }} onClick={() => onRollbackInTable(idx)}>취소</Button>
                        </div>
                        : <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues?.isBooking !== currentValues?.isBooking}>
                            {({ getFieldValue }) =>
                                <div style={{ display: 'flex', gap: '32px', paddingInline: '34px', justifyContent: 'center', alignItems: 'center' }}>
                                    <FormOutlined style={{ paddingInline: '7.8px', fontSize: '24px', color: ((typeof record.id === 'number') || !getFieldValue('isBooking')) ? 'var(--ant-color-icon)' : 'var(--ant-color-text-disabled)', cursor: ((typeof record.id === 'number') || !getFieldValue('isBooking')) ? 'pointer' : 'no-drop' }} onClick={() => ((typeof record.id === 'number') || !getFieldValue('isBooking')) && onChangeInTable(idx, 'isEdit', true)} />
                                    <DeleteOutlined style={{ paddingInline: '7.8px', fontSize: '24px', color: ((typeof record.id === 'number') || !getFieldValue('isBooking')) ? 'var(--ant-color-error)' : 'var(--ant-color-text-disabled)', cursor: ((typeof record.id === 'number') || !getFieldValue('isBooking')) ? 'pointer' : 'no-drop' }} onClick={() => ((typeof record.id === 'number') || !getFieldValue('isBooking')) && onDeleteRow(idx)} />
                                </div>
                            }
                        </Form.Item>
                    }
                </div>
            }])
    ]
    useEffect(() => {
        (editList.length === 0 || isNoEditColumn) && setEditList(dataList);
    }, [dataList]);

    return <>
        <ConfigProvider
            theme={{
                components: {
                    Table: { footerBg: 'transparent' }
                }
            }}
        >
            <Table bordered={bordered} dataSource={editList} rowKey={'id'} columns={columns} pagination={false} footer={(e) => footer?.(e) ?? <div style={{ textAlign: 'right' }}><Button style={{ marginLeft: '20px', border: '1px solid #1677FF' }} type='link' onClick={addNewRow}>{footerText}</Button></div>} />
        </ConfigProvider>
    </>
}

export { CustomDynamicTable }